import React, { useEffect, useState } from 'react';
import { Box, Card, Typography, Breadcrumbs, Divider, Button, Skeleton } from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useResponseHandler } from 'hooks/useResponseHandler';
import validationSchema from './formik/validationSchema';
import assistantsFormModel from './formik/assistantsFormModel';
import { AsistentesForm } from './components/forms/AsistentesForm';
import {
  editAssistantStore,
  getAssistantByIdStore,
  getDataFormDropDownStore,
} from 'store/assistants/thunks';
import { MessagesDrawerRight } from 'components/MessagesDrawerRight';
import formInitialValues from './formik/formInitialValues';
import { assistantsMapper } from './mappers/assistants.mapper';

export const EditAsistentes = () => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { handleResponse, ResponseComponents } = useResponseHandler();
  const { formId, formField } = assistantsFormModel;
  const [dataErrors, setMsgErrors] = useState([]);

  const [initialValues, setInitialValues] = useState<any>(formInitialValues);

  const { form_options, form_options_ia_skills_checkbox } = useSelector(
    (state: any) => state.assistants
  );

  const servicesOptions = form_options || [];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    if (!id) return;

    const response = await dispatch(getAssistantByIdStore(id));
    if (response?.ok && response?.data) {
      const item = response.data;
      const mappedDomain = assistantsMapper.toDomain(item);

      const optionsFromResponse = item.services?.services || [];

      setInitialValues({
        name: mappedDomain.name,
        description: mappedDomain.description || '',
        service_id:
          servicesOptions.find((s: any) => s.value === mappedDomain.serviceId) ||
          (mappedDomain.serviceId && mappedDomain.serviceName
            ? { value: mappedDomain.serviceId, label: mappedDomain.serviceName }
            : undefined),
        systemPrompt: mappedDomain.systemPrompt || '',
        welcomeMessage: mappedDomain.welcomeMessage || '',
        temperature: mappedDomain.temperature ?? '',
        topP: mappedDomain.topP ?? '',
        maxTokens: mappedDomain.maxTokens ?? '',
        avatar: mappedDomain.avatar || '',
        icon: mappedDomain.icon || '',
        color: mappedDomain.color || '',
        visibility:
          mappedDomain.visibility === 'public'
            ? { value: 'public', label: 'Público' }
            : { value: 'private', label: 'Privado' },
        isPublic: mappedDomain.isPublic ?? false,
        allowHistory: mappedDomain.allowHistory ?? true,
        allowImages: mappedDomain.allowImages ?? false,
        allowAudio: mappedDomain.allowAudio ?? false,
        allowFileUpload: mappedDomain.allowFileUpload ?? false,
        maxMessagesPerDay: mappedDomain.maxMessagesPerDay ?? '',
        maxContextMessages: mappedDomain.maxContextMessages ?? '',
        assignedSkills: mappedDomain.assignedSkills ?? {},
      });
    } else {
      navigate('/inteligencia-artificial/asistentes');
    }
  };

  const handleSubmit = async (values: any, actions: any) => {
    if (!id) return;
    actions.setSubmitting(true);

    const dto = assistantsMapper.toDto(values);
    const response = await dispatch(editAssistantStore(id, dto));

    await handleResponse(response, 'Asistente Actualizado Con Éxito', true);
    actions.setSubmitting(false);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <MessagesDrawerRight data={dataErrors} title={''} />
      {ResponseComponents}

      <Typography fontSize={25} color="textPrimary" sx={{ fontWeight: 700 }}>
        Editar Asistente
      </Typography>
      <Breadcrumbs aria-label="breadcrumb" sx={{ padding: 2 }}>
        <Typography color="textPrimary" sx={{ fontWeight: 700 }}>
          Inteligencia Artificial
        </Typography>
        <Link color="inherit" to="/manager/plataforma-ia/asistentes">
          Listar Asistentes
        </Link>
        <Typography color="textPrimary">Editar Asistente</Typography>
      </Breadcrumbs>

      <Divider sx={{ marginBottom: 3 }} />

      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema[0]}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Card
            sx={{
              mt: 2,
              p: 3,
              borderRadius: 3,
              boxShadow: '0px 12px 24px -4px rgba(107, 76, 255, 0.15)',
              background: (theme) =>
                `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.primary.light}08 100%)`,
              border: 'none',
            }}
          >
            <Form id={formId} autoComplete="off">
              <AsistentesForm
                formField={formField}
                servicesOptions={servicesOptions}
                skillsOptions={form_options_ia_skills_checkbox}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
                <Button
                  component={Link}
                  to="/manager/plataforma-ia/asistentes"
                  color="inherit"
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 3,
                    fontWeight: 600,
                    background: 'linear-gradient(45deg, #6b4cff 30%, #a855f7 90%)',
                    boxShadow: '0 4px 14px 0 rgba(107, 76, 255, 0.39)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #583be8 30%, #933ce6 90%)',
                    },
                  }}
                >
                  Guardar Cambios
                </Button>
              </Box>
            </Form>
          </Card>
        )}
      </Formik>
    </Box>
  );
};
